import LegalLayout, {
    LegalSection,
} from "@/components/legal/LegalLayout";
import { CONTACT_EMAIL, CONTACT_PHONE } from "@/lib/brand";

export default function PrivacyPolicy() {
    return (
        <LegalLayout
            testId="privacy-policy-page"
            eyebrow="Legal"
            title="Privacy Policy"
            lastUpdated="December 2025"
            intro="RecoveryDesk respects your privacy. This Privacy Policy explains what information we collect, how we use and protect it, and the choices available to you when you interact with our website and services."
        >
            <LegalSection heading="1. Information We Collect" testId="privacy-info-collected">
                <p>
                    We collect the following categories of information directly
                    from you and through your use of our services:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                    <li>
                        <span className="text-white">Contact information</span>
                        {" "}— including your name, business name, email address,
                        and phone number when you submit our contact form,
                        request information, or otherwise communicate with us.
                    </li>
                    <li>
                        <span className="text-white">Communication content</span>
                        {" "}— the message text you provide via our contact
                        form or in subsequent correspondence with our team.
                    </li>
                    <li>
                        <span className="text-white">Service & usage data</span>
                        {" "}— limited technical information such as IP address,
                        browser type, device information, and timestamps,
                        collected for security, fraud prevention, and
                        operational analytics.
                    </li>
                </ul>
            </LegalSection>

            <LegalSection heading="2. How We Use Your Information" testId="privacy-info-use">
                <p>
                    We use the information we collect to operate, maintain, and
                    improve RecoveryDesk and to provide the services you have
                    requested, including:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                    <li>Responding to inquiries and scheduling consultations.</li>
                    <li>
                        Delivering account, project, and customer-support
                        communications via email or telephone.
                    </li>
                    <li>
                        Operating the underlying web design and development
                        services on behalf of our subscribers.
                    </li>
                    <li>
                        Maintaining the security and integrity of our website
                        and services, including detecting and preventing fraud
                        or abuse.
                    </li>
                    <li>
                        Complying with applicable legal obligations and
                        enforcing our Terms &amp; Conditions.
                    </li>
                </ul>
            </LegalSection>

            <LegalSection heading="3. How We Protect Your Information" testId="privacy-info-protection">
                <p>
                    We take the protection of your information seriously and
                    employ a combination of administrative, technical, and
                    physical safeguards:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                    <li>
                        All data transmitted between your device and
                        RecoveryDesk is encrypted in transit using
                        industry-standard TLS (HTTPS).
                    </li>
                    <li>
                        Stored information is protected behind authenticated,
                        role-restricted systems. Access is limited to authorized
                        personnel on a need-to-know basis.
                    </li>
                    <li>
                        Passwords are stored using one-way cryptographic hashing
                        (bcrypt). We do not store plaintext credentials.
                    </li>
                    <li>
                        We periodically review our practices, vendors, and
                        infrastructure to ensure they meet recognized security
                        standards.
                    </li>
                </ul>
                <p>
                    No method of transmission or storage is perfectly secure.
                    While we work hard to safeguard your information, we cannot
                    guarantee absolute security.
                </p>
            </LegalSection>

            <LegalSection heading="4. Sharing With Third Parties" testId="privacy-third-parties">
                <p>
                    We share information only as necessary to deliver our
                    services and only with service providers who are
                    contractually obligated to protect it. These may include:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                    <li>
                        Email delivery providers that transmit notifications
                        and communications on our behalf.
                    </li>
                    <li>
                        Scheduling and calendar providers used to coordinate
                        consultations and appointments.
                    </li>
                    <li>
                        Hosting and infrastructure providers that operate the
                        systems on which RecoveryDesk is delivered.
                    </li>
                </ul>
                <p>
                    We do not sell your personal information. We do not share
                    your personal information with unrelated third parties for
                    their own marketing purposes.
                </p>

                <p>
                    We may disclose information when required by law, in
                    response to lawful requests by public authorities, or to
                    protect the rights, property, or safety of RecoveryDesk,
                    our users, or others.
                </p>
            </LegalSection>

            <LegalSection heading="5. Your Choices" testId="privacy-your-choices">
                <ul className="list-disc pl-6 space-y-2">
                    <li>
                        <span className="text-white">Email</span>
                        {" "}— You may opt out of non-essential email
                        communications by following the unsubscribe instructions
                        included in those messages or by contacting us directly.
                    </li>
                    <li>
                        <span className="text-white">Access &amp; deletion</span>
                        {" "}— You may request a copy of the information we hold
                        about you, or request that it be corrected or deleted,
                        by contacting us using the details below.
                    </li>
                </ul>
            </LegalSection>

            <LegalSection heading="6. Children's Privacy" testId="privacy-children">
                <p>
                    RecoveryDesk is a business-to-business service and is not
                    directed to children under the age of 13. We do not
                    knowingly collect personal information from anyone under
                    13. If we learn that we have inadvertently collected such
                    information, we will take reasonable steps to delete it.
                </p>
            </LegalSection>

            <LegalSection heading="7. Changes To This Policy" testId="privacy-changes">
                <p>
                    We may update this Privacy Policy from time to time to
                    reflect changes in our services, technology, legal
                    requirements, or business practices. When we do, we will
                    update the &quot;Last Updated&quot; date at the top of this
                    page. Material changes will be highlighted on our website.
                </p>
            </LegalSection>

            <LegalSection heading="8. Contact Us" testId="privacy-contact">
                <p>
                    If you have questions or requests regarding this Privacy
                    Policy or your information, please contact us:
                </p>
                <ul className="list-none pl-0 space-y-1">
                    <li>
                        <span className="text-[#888] mr-2 uppercase tracking-[0.2em] text-[11px]">
                            Email
                        </span>
                        <a
                            href={`mailto:${CONTACT_EMAIL}`}
                            className="text-[#D4AF37] hover:text-[#E5C158] transition-colors"
                        >
                            {CONTACT_EMAIL}
                        </a>
                    </li>
                    <li>
                        <span className="text-[#888] mr-2 uppercase tracking-[0.2em] text-[11px]">
                            Phone
                        </span>
                        <a
                            href={`tel:+1${CONTACT_PHONE.replace(/[^0-9]/g, "")}`}
                            className="text-[#D4AF37] hover:text-[#E5C158] transition-colors"
                        >
                            {CONTACT_PHONE}
                        </a>
                    </li>
                </ul>
            </LegalSection>
        </LegalLayout>
    );
}
